'use client';

import { useState, useCallback, useRef, useLayoutEffect, startTransition, useSyncExternalStore, type FC, type CSSProperties } from 'react';
import { cn } from '../../utils/cn';

interface BlurImageProps {
  src: string;
  srcSet?: string;
  sizes?: string;
  placeholder?: string | null;
  alt: string;
  className?: string;
  style?: CSSProperties;
  loading?: 'eager' | 'lazy';
  fetchPriority?: 'high' | 'low' | 'auto';
}

const subscribeNothing = () => () => {};
const clientSnapshot = () => true;
const serverSnapshot = () => false;

// WHY: с srcset браузер качает кандидата по sizes — проверяем кэш тем же набором; lazy, чтобы проверка сама не качала картинку
const isImageCached = (src: string, srcSet: string | undefined, sizes: string | undefined): boolean => {
  const img = new window.Image();
  img.loading = 'lazy';
  if (srcSet) {
    img.sizes = sizes ?? '';
    img.srcset = srcSet;
  }
  img.src = src;
  return img.complete && img.naturalWidth > 0;
};

export const BlurImage: FC<BlurImageProps> = ({
  src,
  srcSet,
  sizes,
  placeholder,
  alt,
  className,
  style,
  loading,
  fetchPriority,
}) => {
  // WHY: при гидратации снимок false, как на сервере (иначе React #418); при клиентском монтировании true — кэш считаем сразу
  const isClientMount = useSyncExternalStore(subscribeNothing, clientSnapshot, serverSnapshot);
  const [isLoaded, setIsLoaded] = useState(() => isClientMount && isImageCached(src, srcSet, sizes));
  const [hasFailed, setHasFailed] = useState(false);
  const [optimizedFailed, setOptimizedFailed] = useState(false);
  const prevSrcRef = useRef(src);
  const probeRef = useRef<HTMLImageElement>(null);
  // WHY: если оптимизатор не отдал картинку, один раз переключаемся на исходник — иначе карточка осталась бы размытой
  const activeSrcSet = optimizedFailed ? undefined : srcSet;

  useLayoutEffect(() => {
    if (prevSrcRef.current === src) return;
    prevSrcRef.current = src;
    setIsLoaded(false);
    setHasFailed(false);
    setOptimizedFailed(false);
  }, [src]);

  // WHY: после гидратации закэшированную картинку берём из скрытого img и в переходе — чтобы сотни карточек не перерисовывались
  // одной длинной задачей
  useLayoutEffect(() => {
    if (isLoaded) return;
    const probe = probeRef.current;
    if (probe?.complete && probe.naturalWidth > 0) startTransition(() => setIsLoaded(true));
  }, [src, activeSrcSet, sizes, isLoaded]);

  const handleLoad = useCallback(() => {
    setIsLoaded(true);
  }, []);

  const handleError = useCallback(() => {
    if (activeSrcSet) setOptimizedFailed(true);
    else setHasFailed(true);
  }, [activeSrcSet]);

  const showPlaceholder = !!placeholder && (!isLoaded || hasFailed);

  return (
    <div className={cn('overflow-hidden', className)} style={style}>
      <img
        src={showPlaceholder ? placeholder : src}
        srcSet={showPlaceholder ? undefined : activeSrcSet}
        sizes={showPlaceholder || !activeSrcSet ? undefined : sizes}
        alt={alt}
        loading={loading}
        fetchPriority={fetchPriority}
        className={cn('block w-full h-full object-cover', { 'blur-[30px] scale-110': showPlaceholder })}
        onLoad={!showPlaceholder ? handleLoad : undefined}
        onError={!showPlaceholder && activeSrcSet ? handleError : undefined}
      />

      {showPlaceholder && !hasFailed && (
        <img
          src={src}
          srcSet={activeSrcSet}
          sizes={activeSrcSet ? sizes : undefined}
          alt=""
          loading={loading}
          fetchPriority={fetchPriority}
          ref={probeRef}
          className="absolute w-px h-px opacity-0 pointer-events-none"
          onLoad={handleLoad}
          onError={handleError}
          aria-hidden="true"
        />
      )}
    </div>
  );
};
